import React, { useState } from "react";
import axios from "axios";
import { 
  Phone, MessageSquare, Award, Users, BookOpen, ThumbsUp, 
  Clock, Star, MapPin, Mail, CheckCircle2, ShieldCheck, 
  HelpCircle, Menu, X, Check, ArrowRight
} from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_4b64a723-9d53-443a-b493-5b31ade704cd/artifacts/tn4p4ojp_Screenshot%202026-07-01%20at%202.21.26%E2%80%AFPM.png";
const HERO_PERSON = "https://customer-assets.emergentagent.com/job_4b64a723-9d53-443a-b493-5b31ade704cd/artifacts/l2c3812a_WhatsApp%20Image%202026-06-11%20at%2013.15.19.jpeg";

export default function LandingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [enquiry, setEnquiry] = useState({ name: "", phone: "", email: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const trackClick = async (buttonId) => {
    try {
      await axios.post(`${API}/analytics/click`, { button_id: buttonId });
    } catch (e) {
      console.error("Click tracking failed", e);
    }
  };

  const handleWhatsAppClick = (buttonId, prefilledText) => {
    trackClick(buttonId);
    const encodedText = encodeURIComponent(prefilledText);
    window.open(`https://wa.me/919534263712?text=${encodedText}`, "_blank");
  };

  const handleCallClick = (buttonId) => {
    trackClick(buttonId);
    window.open("tel:9534263712", "_self");
  };

  const handleEnquirySubmit = async (e) => {
    e.preventDefault();
    if (!enquiry.name || !enquiry.phone || !enquiry.email) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/enquiries`, enquiry);
      toast.success("Enquiry submitted successfully! SMS and Email notifications simulated.");
      setEnquiry({ name: "", phone: "", email: "", message: "" });
    } catch (err) {
      toast.error("Failed to submit enquiry. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] text-[#111827] selection:bg-[#D4AF37] selection:text-white overflow-x-hidden">
      
      {/* Sticky Navigation Bar */}
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-gray-200/50 transition-all duration-300">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <a href="#home" className="flex items-center gap-3" data-testid="nav-brand-logo">
            <img src={LOGO_URL} alt="Jijivisha Logo" className="h-12 w-12 object-contain rounded-lg" />
            <div className="flex flex-col">
              <span className="font-display text-xl font-bold tracking-tight text-[#0A1E3F]">Jijivisha</span>
              <span className="text-xs uppercase tracking-widest text-[#D4AF37] font-semibold">Consulting</span>
            </div>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            <a href="#home" className="text-sm font-medium hover:text-[#D4AF37] transition-colors" data-testid="nav-home-link">Home</a>
            <a href="#about" className="text-sm font-medium hover:text-[#D4AF37] transition-colors" data-testid="nav-about-link">About</a>
            <a href="#why-choose-us" className="text-sm font-medium hover:text-[#D4AF37] transition-colors" data-testid="nav-why-choose-us-link">Why Us</a>
            <a href="#services" className="text-sm font-medium hover:text-[#D4AF37] transition-colors" data-testid="nav-services-link">Services</a>
            <a href="#testimonials" className="text-sm font-medium hover:text-[#D4AF37] transition-colors" data-testid="nav-testimonials-link">Testimonials</a>
            <a href="#contact" className="text-sm font-medium hover:text-[#D4AF37] transition-colors" data-testid="nav-contact-link">Contact</a>
            
            <button 
              onClick={() => handleWhatsAppClick("whatsapp-header", "Hello Jijivisha Consulting, I'd like to book a career consultation session with Anand Shankar.")}
              className="bg-[#0A1E3F] text-white hover:bg-[#162F56] rounded-full px-5 py-2.5 text-xs font-semibold uppercase tracking-wider transition-all shadow-md"
              data-testid="nav-book-btn"
            >
              Book Consultation
            </button>
          </div>

          {/* Mobile Hamburg menu button */}
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden text-[#0A1E3F] p-2 hover:bg-gray-100 rounded-lg transition-colors"
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation Drawer */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-b border-gray-200 shadow-lg px-6 py-4 flex flex-col gap-4 animate-accordion-down">
            <a href="#home" onClick={() => setMobileMenuOpen(false)} className="py-2 border-b border-gray-50 text-sm font-medium hover:text-[#D4AF37]" data-testid="nav-home-link-mobile">Home</a>
            <a href="#about" onClick={() => setMobileMenuOpen(false)} className="py-2 border-b border-gray-50 text-sm font-medium hover:text-[#D4AF37]" data-testid="nav-about-link-mobile">About</a>
            <a href="#why-choose-us" onClick={() => setMobileMenuOpen(false)} className="py-2 border-b border-gray-50 text-sm font-medium hover:text-[#D4AF37]" data-testid="nav-why-choose-us-link-mobile">Why Us</a>
            <a href="#services" onClick={() => setMobileMenuOpen(false)} className="py-2 border-b border-gray-50 text-sm font-medium hover:text-[#D4AF37]" data-testid="nav-services-link-mobile">Services</a>
            <a href="#testimonials" onClick={() => setMobileMenuOpen(false)} className="py-2 border-b border-gray-50 text-sm font-medium hover:text-[#D4AF37]" data-testid="nav-testimonials-link-mobile">Testimonials</a>
            <a href="#contact" onClick={() => setMobileMenuOpen(false)} className="py-2 border-b border-gray-50 text-sm font-medium hover:text-[#D4AF37]" data-testid="nav-contact-link-mobile">Contact</a>
            
            <button 
              onClick={() => {
                setMobileMenuOpen(false);
                handleWhatsAppClick("whatsapp-mobile-drawer", "Hello Jijivisha Consulting, I want to book a session.");
              }}
              className="bg-[#0A1E3F] text-white hover:bg-[#162F56] rounded-full py-3 text-center text-sm font-semibold tracking-wider uppercase transition-all shadow-md w-full"
              data-testid="nav-book-btn-mobile"
            >
              Book Consultation
            </button>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative py-16 md:py-24 max-w-7xl mx-auto px-6">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Hero text */}
          <div className="space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#0A1E3F] text-xs font-semibold uppercase tracking-wider rounded-full">
              <Award size={14} className="text-[#D4AF37]" />
              Premium Career & HR Mentorship
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-[#0A1E3F] leading-tight font-display">
              You deserve an expert and high quality service consultancy & training
            </h1>
            
            <p className="text-[#4B5563] text-base md:text-lg leading-relaxed font-light">
              Whether you&apos;re in search of a right university (college), preparing for competitive interviews, or planning your career or seeking legal or HR related advice, receive one-on-one mentorship from an expert with <strong className="font-semibold text-[#0A1E3F]">28+ years</strong> of related experience.
            </p>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 pt-4">
              <button 
                onClick={() => handleWhatsAppClick("whatsapp-hero", "Hello Anand Shankar, I would like to book a one-on-one career consultation session.")}
                className="bg-[#D4AF37] text-white hover:bg-[#C5A059] rounded-full px-8 py-4 font-semibold tracking-wider uppercase flex items-center justify-center gap-2 transition-all shadow-lg shadow-[#D4AF37]/30"
                data-testid="hero-book-consultation-button"
              >
                <MessageSquare size={18} />
                Book Consultation
              </button>
              
              <div className="flex flex-col text-center sm:text-left text-xs text-[#4B5563] p-1 border-l-0 sm:border-l sm:border-gray-200 sm:pl-4">
                <span className="font-semibold text-[#0A1E3F]">WhatsApp Only</span>
                <span className="text-[#D4AF37] font-bold text-sm tracking-wide">9534263712</span>
              </div>
            </div>
          </div>

          {/* Hero image with premium treat */}
          <div className="relative flex justify-center">
            <div className="absolute inset-0 bg-gradient-to-r from-[#D4AF37]/10 to-[#0A1E3F]/5 rounded-3xl transform rotate-3 scale-105 -z-10"></div>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-white aspect-[4/5] max-w-sm md:max-w-md w-full bg-slate-100">
              <img 
                src={HERO_PERSON} 
                alt="Anand Shankar" 
                className="w-full h-full object-cover object-top" 
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A1E3F]/40 to-transparent"></div>
              
              <div className="absolute bottom-6 left-6 right-6 bg-white/90 backdrop-blur-md p-4 rounded-xl shadow-lg border border-white/50">
                <p className="text-xs uppercase tracking-widest text-[#D4AF37] font-bold">Expert Advisor</p>
                <h3 className="font-display font-bold text-lg text-[#0A1E3F]">Anand Shankar</h3>
                <p className="text-[11px] text-gray-600">28+ Years of Practical Experience</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Achievements / Counters Section */}
      <section className="bg-[#0A1E3F] text-white py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div className="space-y-2">
              <h2 className="text-4xl sm:text-5xl font-bold font-display text-[#D4AF37]" data-testid="stat-experience">28+</h2>
              <p className="text-xs uppercase tracking-widest text-gray-300 font-medium">Years Experience</p>
            </div>
            <div className="space-y-2">
              <h2 className="text-4xl sm:text-5xl font-bold font-display text-[#D4AF37]" data-testid="stat-mentored">5000+</h2>
              <p className="text-xs uppercase tracking-widest text-gray-300 font-medium">People Mentored</p>
            </div>
            <div className="space-y-2">
              <h2 className="text-4xl sm:text-5xl font-bold font-display text-[#D4AF37]" data-testid="stat-sessions">1000+</h2>
              <p className="text-xs uppercase tracking-widest text-gray-300 font-medium">Interview Sessions</p>
            </div>
            <div className="space-y-2">
              <h2 className="text-4xl sm:text-5xl font-bold font-display text-[#D4AF37]" data-testid="stat-satisfaction">98%</h2>
              <p className="text-xs uppercase tracking-widest text-gray-300 font-medium">Client Satisfaction</p>
            </div>
          </div>
        </div>
      </section>

      {/* About Anand Shankar Section */}
      <section id="about" className="py-20 max-w-7xl mx-auto px-6">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div className="order-2 md:order-1">
            <div className="relative rounded-2xl overflow-hidden shadow-xl border border-gray-100 bg-white p-2">
              <img 
                src="https://images.pexels.com/photos/15202224/pexels-photo-15202224.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" 
                alt="Corporate Office" 
                className="rounded-xl w-full h-[350px] object-cover"
              />
              <div className="absolute inset-0 bg-[#0A1E3F]/10"></div>
            </div>
          </div>

          <div className="space-y-6 order-1 md:order-2">
            <p className="text-[#D4AF37] text-sm uppercase tracking-widest font-bold">About Anand Shankar</p>
            <h2 className="text-3xl sm:text-4xl font-semibold font-display text-[#0A1E3F]" data-testid="about-heading">
              Anand Shankar
            </h2>
            <p className="text-sm font-medium text-gray-500 uppercase tracking-wider">
              Career Consultant | HR & legal Expert | career & Interview Mentor
            </p>
            <div className="h-0.5 w-16 bg-[#D4AF37]"></div>
            
            <p className="text-[#4B5563] text-sm leading-relaxed">
              With over 28 years of professional experience, Anand Shankar has worked with:
            </p>
            <ul className="space-y-3 text-sm text-[#4B5563]">
              <li className="flex items-start gap-3">
                <CheckCircle2 size={16} className="text-[#D4AF37] mt-1 shrink-0" />
                <span>Leading MNCs in HR operations and talent development</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 size={16} className="text-[#D4AF37] mt-1 shrink-0" />
                <span>Government Agencies and public sector advisory</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 size={16} className="text-[#D4AF37] mt-1 shrink-0" />
                <span>HR Departments & Large-scale Recruitment Projects</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 size={16} className="text-[#D4AF37] mt-1 shrink-0" />
                <span>Providing legal, HR consultancy and training to individuals & companies</span>
              </li>
            </ul>

            <p className="text-[#4B5563] text-sm leading-relaxed italic">
              &quot;He has successfully mentored more than 5000+ students and professionals in choosing the right career path, preparing for interviews, and achieving their dream jobs. His vast practical industry knowledge helps candidates to understand what recruiters actually look for.&quot;
            </p>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section id="why-choose-us" className="bg-[#0A1E3F]/5 py-20">
        <div className="max-w-7xl mx-auto px-6 space-y-12">
          <div className="text-center space-y-4">
            <p className="text-[#D4AF37] text-sm uppercase tracking-widest font-bold">Unrivaled Expertise</p>
            <h2 className="text-3xl sm:text-4xl font-semibold font-display text-[#0A1E3F]">Why Choose Us</h2>
            <div className="h-0.5 w-16 bg-[#D4AF37] mx-auto"></div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-2xl shadow-xl shadow-slate-200/50 hover:scale-[1.03] transition-transform duration-300 border border-gray-100 flex flex-col justify-between">
              <div className="space-y-4">
                <div className="p-3 bg-[#0A1E3F]/5 text-[#D4AF37] rounded-xl w-fit">
                  <Award size={24} />
                </div>
                <h3 className="font-display font-semibold text-lg text-[#0A1E3F]">28+ Years Experience</h3>
                <p className="text-xs text-[#4B5563] leading-relaxed">Nearly three decades of high-level career consulting, corporate structuring, and mentorship expertise.</p>
              </div>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-xl shadow-slate-200/50 hover:scale-[1.03] transition-transform duration-300 border border-gray-100 flex flex-col justify-between">
              <div className="space-y-4">
                <div className="p-3 bg-[#0A1E3F]/5 text-[#D4AF37] rounded-xl w-fit">
                  <Users size={24} />
                </div>
                <h3 className="font-display font-semibold text-lg text-[#0A1E3F]">5000+ Success Stories</h3>
                <p className="text-xs text-[#4B5563] leading-relaxed">Thousands of students and professionals successfully guided to land their dream positions in MNCs & Government sectors.</p>
              </div>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-xl shadow-slate-200/50 hover:scale-[1.03] transition-transform duration-300 border border-gray-100 flex flex-col justify-between">
              <div className="space-y-4">
                <div className="p-3 bg-[#0A1E3F]/5 text-[#D4AF37] rounded-xl w-fit">
                  <ShieldCheck size={24} />
                </div>
                <h3 className="font-display font-semibold text-lg text-[#0A1E3F]">HR & Legal Expert</h3>
                <p className="text-xs text-[#4B5563] leading-relaxed">Invaluable insider knowledge of complex hiring processes, regulatory labor structures, and legal compliance.</p>
              </div>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-xl shadow-slate-200/50 hover:scale-[1.03] transition-transform duration-300 border border-gray-100 flex flex-col justify-between">
              <div className="space-y-4">
                <div className="p-3 bg-[#0A1E3F]/5 text-[#D4AF37] rounded-xl w-fit">
                  <BookOpen size={24} />
                </div>
                <h3 className="font-display font-semibold text-lg text-[#0A1E3F]">Interview Coaching</h3>
                <p className="text-xs text-[#4B5563] leading-relaxed">Comprehensive, customized mock interview prep for MNCs, corporate agencies, and highly selective Government interviews.</p>
              </div>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-xl shadow-slate-200/50 hover:scale-[1.03] transition-transform duration-300 border border-gray-100 flex flex-col justify-between">
              <div className="space-y-4">
                <div className="p-3 bg-[#0A1E3F]/5 text-[#D4AF37] rounded-xl w-fit">
                  <Clock size={24} />
                </div>
                <h3 className="font-display font-semibold text-lg text-[#0A1E3F]">Career Planning</h3>
                <p className="text-xs text-[#4B5563] leading-relaxed">Personalized career roadmaps carefully calibrated with your natural skills, ambitions, and immediate professional goals.</p>
              </div>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-xl shadow-slate-200/50 hover:scale-[1.03] transition-transform duration-300 border border-gray-100 flex flex-col justify-between">
              <div className="space-y-4">
                <div className="p-3 bg-[#0A1E3F]/5 text-[#D4AF37] rounded-xl w-fit">
                  <Award size={24} />
                </div>
                <h3 className="font-display font-semibold text-lg text-[#0A1E3F]">College Selection</h3>
                <p className="text-xs text-[#4B5563] leading-relaxed">Expert analytical guidance in choosing the ideal college, branch, and training streams that align with high-growth market demand.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 max-w-7xl mx-auto px-6 space-y-12">
        <div className="text-center space-y-4">
          <p className="text-[#D4AF37] text-sm uppercase tracking-widest font-bold">Our Offerings</p>
          <h2 className="text-3xl sm:text-4xl font-semibold font-display text-[#0A1E3F]">Our Premium Services</h2>
          <div className="h-0.5 w-16 bg-[#D4AF37] mx-auto"></div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div className="bg-white p-8 rounded-2xl border border-gray-100 shadow-md hover:border-[#D4AF37] hover:shadow-xl transition-all duration-300 flex flex-col justify-between">
            <div className="space-y-4">
              <h3 className="font-display font-semibold text-xl text-[#0A1E3F]">Career Counselling</h3>
              <p className="text-xs text-gray-600">Receive a detailed, bespoke roadmap engineered specifically for your strengths and professional ambitions.</p>
              <div className="h-px bg-gray-100"></div>
              <ul className="space-y-2 text-xs text-gray-500">
                <li className="flex items-center gap-2"><Check size={14} className="text-[#D4AF37]" /> Personalized SWOT Analysis</li>
                <li className="flex items-center gap-2"><Check size={14} className="text-[#D4AF37]" /> Core skill matching</li>
                <li className="flex items-center gap-2"><Check size={14} className="text-[#D4AF37]" /> Goal setting milestones</li>
              </ul>
            </div>
          </div>

          <div className="bg-white p-8 rounded-2xl border border-gray-100 shadow-md hover:border-[#D4AF37] hover:shadow-xl transition-all duration-300 flex flex-col justify-between">
            <div className="space-y-4">
              <h3 className="font-display font-semibold text-xl text-[#0A1E3F]">College Selection Guidance</h3>
              <p className="text-xs text-gray-600">Expert, objective analysis of Indian & global colleges to ensure you get maximum value and ROI from your education.</p>
              <div className="h-px bg-gray-100"></div>
              <ul className="space-y-2 text-xs text-gray-500">
                <li className="flex items-center gap-2"><Check size={14} className="text-[#D4AF37]" /> University ranking reviews</li>
                <li className="flex items-center gap-2"><Check size={14} className="text-[#D4AF37]" /> Stream & major choice assistance</li>
                <li className="flex items-center gap-2"><Check size={14} className="text-[#D4AF37]" /> Placement analytics review</li>
              </ul>
            </div>
          </div>

          <div className="bg-white p-8 rounded-2xl border border-gray-100 shadow-md hover:border-[#D4AF37] hover:shadow-xl transition-all duration-300 flex flex-col justify-between">
            <div className="space-y-4">
              <h3 className="font-display font-semibold text-xl text-[#0A1E3F]">Interview Preparation</h3>
              <p className="text-xs text-gray-600">Highly customized interview simulations designed with 28+ years of core HR recruiting experience.</p>
              <div className="h-px bg-gray-100"></div>
              <ul className="space-y-2 text-xs text-gray-500">
                <li className="flex items-center gap-2"><Check size={14} className="text-[#D4AF37]" /> Mock HR Interview Sessions</li>
                <li className="flex items-center gap-2"><Check size={14} className="text-[#D4AF37]" /> Honest, analytical Resume Feedback</li>
                <li className="flex items-center gap-2"><Check size={14} className="text-[#D4AF37]" /> Confidence building & Voice grooming</li>
              </ul>
            </div>
          </div>

          <div className="bg-white p-8 rounded-2xl border border-gray-100 shadow-md hover:border-[#D4AF37] hover:shadow-xl transition-all duration-300 flex flex-col justify-between">
            <div className="space-y-4">
              <h3 className="font-display font-semibold text-xl text-[#0A1E3F]">Corporate Career Guidance</h3>
              <p className="text-xs text-gray-600">Strategic career recalibration for existing professionals seeking faster promotion, role transition or salary jump.</p>
              <div className="h-px bg-gray-100"></div>
              <ul className="space-y-2 text-xs text-gray-500">
                <li className="flex items-center gap-2"><Check size={14} className="text-[#D4AF37]" /> Resume restructuring for executive roles</li>
                <li className="flex items-center gap-2"><Check size={14} className="text-[#D4AF37]" /> Leadership communication coaching</li>
                <li className="flex items-center gap-2"><Check size={14} className="text-[#D4AF37]" /> Conflict resolution advisory</li>
              </ul>
            </div>
          </div>

          <div className="bg-white p-8 rounded-2xl border border-gray-100 shadow-md hover:border-[#D4AF37] hover:shadow-xl transition-all duration-300 flex flex-col justify-between">
            <div className="space-y-4">
              <h3 className="font-display font-semibold text-xl text-[#0A1E3F]">Government Career Guidance</h3>
              <p className="text-xs text-gray-600">Specialized structural mentorship for aspirants eyeing prestigious administrative, government, and legal positions.</p>
              <div className="h-px bg-gray-100"></div>
              <ul className="space-y-2 text-xs text-gray-500">
                <li className="flex items-center gap-2"><Check size={14} className="text-[#D4AF37]" /> Exam structuring timelines</li>
                <li className="flex items-center gap-2"><Check size={14} className="text-[#D4AF37]" /> Verbal boards simulation</li>
                <li className="flex items-center gap-2"><Check size={14} className="text-[#D4AF37]" /> Presentation of administrative viewpoints</li>
              </ul>
            </div>
          </div>

          <div className="bg-white p-8 rounded-2xl border border-gray-100 shadow-md hover:border-[#D4AF37] hover:shadow-xl transition-all duration-300 flex flex-col justify-between">
            <div className="space-y-4">
              <h3 className="font-display font-semibold text-xl text-[#0A1E3F]">Legal & HR Training</h3>
              <p className="text-xs text-gray-600">Comprehensive structural training and legal compliance advisory for companies, HR professionals, and recruitment experts.</p>
              <div className="h-px bg-gray-100"></div>
              <ul className="space-y-2 text-xs text-gray-500">
                <li className="flex items-center gap-2"><Check size={14} className="text-[#D4AF37]" /> Corporate policy designing</li>
                <li className="flex items-center gap-2"><Check size={14} className="text-[#D4AF37]" /> Labor regulations training</li>
                <li className="flex items-center gap-2"><Check size={14} className="text-[#D4AF37]" /> Recruitment optimization blueprints</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Consultation Info (₹1500 box) */}
      <section className="bg-gradient-to-br from-[#0A1E3F] to-[#162F56] text-white py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-white/5 backdrop-blur-md rounded-3xl p-8 md:p-12 border border-white/10 max-w-4xl mx-auto text-center space-y-8">
            <p className="text-[#D4AF37] text-sm uppercase tracking-widest font-bold">One-on-One Session</p>
            <h2 className="text-3xl sm:text-5xl font-display font-bold">Exclusive Career Consultation</h2>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 py-4">
              <div className="bg-white/10 rounded-2xl px-8 py-4 border border-white/5">
                <span className="text-xs uppercase text-gray-300 block tracking-widest">Investment</span>
                <span className="text-3xl font-bold font-display text-[#D4AF37]">₹1500</span>
              </div>
              <div className="bg-white/10 rounded-2xl px-8 py-4 border border-white/5">
                <span className="text-xs uppercase text-gray-300 block tracking-widest">Duration</span>
                <span className="text-3xl font-bold font-display text-[#D4AF37]">1 Hour</span>
              </div>
            </div>

            <div className="bg-[#D4AF37]/10 rounded-2xl p-6 border border-[#D4AF37]/30 text-sm max-w-2xl mx-auto text-gray-200">
              <p className="leading-relaxed">
                <strong>💡 Note:</strong> One consultation can completely transform your career direction with guidance backed by 28+ years of real HR experience. Get the insights recruiters never tell you directly!
              </p>
            </div>

            <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
              <button 
                onClick={() => handleWhatsAppClick("whatsapp-booking", "Hello Anand Shankar, I'm booking a ₹1500 career consultation session.")}
                className="bg-[#D4AF37] text-white hover:bg-[#C5A059] rounded-full px-8 py-4 font-semibold tracking-wider uppercase flex items-center gap-2 transition-all w-full sm:w-auto justify-center"
                data-testid="booking-now-button"
              >
                Book Now
              </button>
              <button 
                onClick={() => handleCallClick("call-now")}
                className="border border-white hover:bg-white/10 text-white rounded-full px-8 py-4 font-semibold tracking-wider uppercase flex items-center gap-2 transition-all w-full sm:w-auto justify-center"
                data-testid="call-now-button"
              >
                <Phone size={18} />
                Call Now
              </button>
            </div>
            <p className="text-xs text-gray-400">Consultation bookings are prioritized on WhatsApp only.</p>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-20 max-w-7xl mx-auto px-6 space-y-12">
        <div className="text-center space-y-4">
          <p className="text-[#D4AF37] text-sm uppercase tracking-widest font-bold">Kind Words</p>
          <h2 className="text-3xl sm:text-4xl font-semibold font-display text-[#0A1E3F]">Client Testimonials</h2>
          <div className="h-0.5 w-16 bg-[#D4AF37] mx-auto"></div>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100 flex flex-col justify-between" data-testid="testimonial-card">
            <div className="space-y-4">
              <div className="flex text-[#D4AF37] gap-1">
                <Star size={16} fill="#D4AF37" />
                <Star size={16} fill="#D4AF37" />
                <Star size={16} fill="#D4AF37" />
                <Star size={16} fill="#D4AF37" />
                <Star size={16} fill="#D4AF37" />
              </div>
              <p className="font-display italic text-[#4B5563] text-sm leading-relaxed">
                &quot;I finally selected the right career path after years of confusion. The college guidance was absolutely spot-on.&quot;
              </p>
            </div>
            <div className="pt-6 border-t border-gray-50 flex items-center gap-3">
              <div className="h-10 w-10 bg-[#0A1E3F]/10 rounded-full flex items-center justify-center font-bold text-[#0A1E3F]">
                R
              </div>
              <div>
                <h4 className="font-semibold text-xs text-[#0A1E3F]">Rahul Sen</h4>
                <p className="text-[10px] text-gray-500">Corporate Aspirant</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100 flex flex-col justify-between" data-testid="testimonial-card">
            <div className="space-y-4">
              <div className="flex text-[#D4AF37] gap-1">
                <Star size={16} fill="#D4AF37" />
                <Star size={16} fill="#D4AF37" />
                <Star size={16} fill="#D4AF37" />
                <Star size={16} fill="#D4AF37" />
                <Star size={16} fill="#D4AF37" />
              </div>
              <p className="font-display italic text-[#4B5563] text-sm leading-relaxed">
                &quot;The interview guidance helped me crack my corporate interview with a top MNC. Truly real-world HR advisory.&quot;
              </p>
            </div>
            <div className="pt-6 border-t border-gray-50 flex items-center gap-3">
              <div className="h-10 w-10 bg-[#0A1E3F]/10 rounded-full flex items-center justify-center font-bold text-[#0A1E3F]">
                P
              </div>
              <div>
                <h4 className="font-semibold text-xs text-[#0A1E3F]">Priya Sharma</h4>
                <p className="text-[10px] text-gray-500">Software Engineer</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100 flex flex-col justify-between" data-testid="testimonial-card">
            <div className="space-y-4">
              <div className="flex text-[#D4AF37] gap-1">
                <Star size={16} fill="#D4AF37" />
                <Star size={16} fill="#D4AF37" />
                <Star size={16} fill="#D4AF37" />
                <Star size={16} fill="#D4AF37" />
                <Star size={16} fill="#D4AF37" />
              </div>
              <p className="font-display italic text-[#4B5563] text-sm leading-relaxed">
                &quot;The best investment I made for my career. The communication and confidence coaching transformed my public speaking.&quot;
              </p>
            </div>
            <div className="pt-6 border-t border-gray-50 flex items-center gap-3">
              <div className="h-10 w-10 bg-[#0A1E3F]/10 rounded-full flex items-center justify-center font-bold text-[#0A1E3F]">
                A
              </div>
              <div>
                <h4 className="font-semibold text-xs text-[#0A1E3F]">Animesh Roy</h4>
                <p className="text-[10px] text-gray-500">Management Trainee</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Founder & Mentors Section */}
      <section className="bg-[#0A1E3F]/5 py-20">
        <div className="max-w-7xl mx-auto px-6 space-y-12">
          <div className="text-center space-y-4">
            <p className="text-[#D4AF37] text-sm uppercase tracking-widest font-bold">Our Visionary Leadership</p>
            <h2 className="text-3xl sm:text-4xl font-semibold font-display text-[#0A1E3F]">The Team Behind Jijivisha</h2>
            <div className="h-0.5 w-16 bg-[#D4AF37] mx-auto"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-12 max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-md text-center space-y-4">
              <div className="h-24 w-24 bg-[#0A1E3F]/10 text-[#0A1E3F] rounded-full mx-auto flex items-center justify-center text-3xl font-display font-bold">
                KS
              </div>
              <div>
                <h3 className="font-display font-semibold text-xl text-[#0A1E3F]">Khyati Shankar</h3>
                <p className="text-xs uppercase tracking-widest text-[#D4AF37] font-bold">Founder & CEO</p>
              </div>
              <p className="text-xs text-[#4B5563] leading-relaxed">
                Visionary behind Jijivisha Consulting. Committed to engineering dynamic pathways and opportunities for career development across sectors.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-md text-center space-y-4">
              <img 
                src={HERO_PERSON} 
                alt="Anand Shankar" 
                className="h-24 w-24 object-cover object-top rounded-full mx-auto border-2 border-[#D4AF37]" 
              />
              <div>
                <h3 className="font-display font-semibold text-xl text-[#0A1E3F]">Anand Shankar</h3>
                <p className="text-xs uppercase tracking-widest text-[#D4AF37] font-bold">Chief Mentor & Career Advisor</p>
              </div>
              <p className="text-xs text-[#4B5563] leading-relaxed">
                Career Consultant & HR Specialist with 28+ years of expertise. Delivered practical mentorship to thousands of students, corporate legal professionals.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 max-w-7xl mx-auto px-6 space-y-12">
        <div className="text-center space-y-4">
          <p className="text-[#D4AF37] text-sm uppercase tracking-widest font-bold">Get In Touch</p>
          <h2 className="text-3xl sm:text-4xl font-semibold font-display text-[#0A1E3F]">Contact Us & Enquire</h2>
          <div className="h-0.5 w-16 bg-[#D4AF37] mx-auto"></div>
        </div>

        <div className="grid md:grid-cols-12 gap-12">
          {/* Contact Details */}
          <div className="md:col-span-5 space-y-8 bg-[#0A1E3F] text-white p-8 rounded-3xl">
            <h3 className="font-display font-semibold text-2xl text-[#D4AF37]">Contact Information</h3>
            <p className="text-sm text-gray-300 font-light leading-relaxed">
              Have questions? Submit an enquiry form or call us directly. We respond to all queries within 24 hours.
            </p>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-white/10 rounded-xl text-[#D4AF37]">
                  <Phone size={18} />
                </div>
                <div>
                  <p className="text-xs uppercase text-gray-400">Phone Support</p>
                  <p className="font-semibold text-sm">7295047308</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="p-3 bg-white/10 rounded-xl text-[#D4AF37]">
                  <MessageSquare size={18} />
                </div>
                <div>
                  <p className="text-xs uppercase text-gray-400">WhatsApp Booking Only</p>
                  <p className="font-semibold text-sm text-[#D4AF37]">9534263712</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="p-3 bg-white/10 rounded-xl text-[#D4AF37]">
                  <Mail size={18} />
                </div>
                <div>
                  <p className="text-xs uppercase text-gray-400">Email Address</p>
                  <p className="font-semibold text-sm">jijivishaconsulting@gmail.com</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="p-3 bg-white/10 rounded-xl text-[#D4AF37]">
                  <MapPin size={18} />
                </div>
                <div>
                  <p className="text-xs uppercase text-gray-400">Location Office</p>
                  <p className="font-semibold text-sm">Remote & Offline Premium Training</p>
                </div>
              </div>
            </div>

            {/* Google Map Placeholder */}
            <div className="bg-white/5 rounded-2xl h-36 border border-white/10 flex items-center justify-center relative overflow-hidden">
              <MapPin size={32} className="text-[#D4AF37] animate-pulse z-10" />
              <div className="absolute inset-0 bg-slate-800 opacity-40"></div>
              <p className="absolute bottom-4 text-xs font-semibold text-gray-300">Google Map Location Services</p>
            </div>
          </div>

          {/* Enquiry Form */}
          <div className="md:col-span-7 bg-white p-8 rounded-3xl border border-gray-100 shadow-xl">
            <h3 className="font-display font-semibold text-xl text-[#0A1E3F] mb-6">Send an Online Enquiry</h3>
            
            <form onSubmit={handleEnquirySubmit} className="space-y-5">
              <div className="space-y-1">
                <label className="text-xs font-semibold text-gray-500 uppercase">Your Name <span className="text-red-500">*</span></label>
                <input 
                  type="text" 
                  value={enquiry.name}
                  onChange={(e) => setEnquiry({ ...enquiry, name: e.target.value })}
                  placeholder="Enter your full name" 
                  className="w-full border border-gray-200 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none rounded-xl px-4 py-3 text-sm transition-all"
                  required
                  data-testid="enquiry-name-input"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-gray-500 uppercase">Phone Number <span className="text-red-500">*</span></label>
                  <input 
                    type="tel" 
                    value={enquiry.phone}
                    onChange={(e) => setEnquiry({ ...enquiry, phone: e.target.value })}
                    placeholder="Enter 10-digit number" 
                    className="w-full border border-gray-200 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none rounded-xl px-4 py-3 text-sm transition-all"
                    required
                    data-testid="enquiry-phone-input"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-gray-500 uppercase">Email Address <span className="text-red-500">*</span></label>
                  <input 
                    type="email" 
                    value={enquiry.email}
                    onChange={(e) => setEnquiry({ ...enquiry, email: e.target.value })}
                    placeholder="example@gmail.com" 
                    className="w-full border border-gray-200 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none rounded-xl px-4 py-3 text-sm transition-all"
                    required
                    data-testid="enquiry-email-input"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-gray-500 uppercase">Message</label>
                <textarea 
                  rows={4}
                  value={enquiry.message}
                  onChange={(e) => setEnquiry({ ...enquiry, message: e.target.value })}
                  placeholder="Tell us about your career path, college choice or interview needs" 
                  className="w-full border border-gray-200 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none rounded-xl px-4 py-3 text-sm transition-all"
                  data-testid="enquiry-message-input"
                ></textarea>
              </div>

              <button 
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#0A1E3F] hover:bg-[#162F56] text-white py-4 rounded-xl font-semibold tracking-wider uppercase transition-all shadow-md flex items-center justify-center gap-2"
                data-testid="enquiry-submit-button"
              >
                {isSubmitting ? "Submitting..." : "Submit Enquiry"}
                <ArrowRight size={16} />
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 bg-[#0A1E3F]/5">
        <div className="max-w-4xl mx-auto px-6 space-y-12">
          <div className="text-center space-y-4">
            <p className="text-[#D4AF37] text-sm uppercase tracking-widest font-bold">Frequently Asked Questions</p>
            <h2 className="text-3xl sm:text-4xl font-semibold font-display text-[#0A1E3F]">Got Questions? We Have Answers</h2>
            <div className="h-0.5 w-16 bg-[#D4AF37] mx-auto"></div>
          </div>

          <Accordion type="single" collapsible className="w-full space-y-4" data-testid="faq-accordion">
            <AccordionItem value="faq-1" className="bg-white rounded-2xl border border-gray-200/50 overflow-hidden px-6">
              <AccordionTrigger className="text-sm font-semibold text-[#0A1E3F] hover:no-underline py-5" data-testid="faq-trigger-1">
                How long is the consultation?
              </AccordionTrigger>
              <AccordionContent className="text-xs text-gray-600 leading-relaxed pb-5">
                Each exclusive consultation session is strictly 1 Hour long. It includes direct, focused one-on-one video/voice advisory backed by 28+ years of high level corporate recruiting experience.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="faq-2" className="bg-white rounded-2xl border border-gray-200/50 overflow-hidden px-6">
              <AccordionTrigger className="text-sm font-semibold text-[#0A1E3F] hover:no-underline py-5" data-testid="faq-trigger-2">
                Who should book a consultation?
              </AccordionTrigger>
              <AccordionContent className="text-xs text-gray-600 leading-relaxed pb-5">
                Students searching for the right university/stream, fresh graduates prepping for corporate interviews, or professionals seeking mid-level structural career transitions or legal/HR advisory.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="faq-3" className="bg-white rounded-2xl border border-gray-200/50 overflow-hidden px-6">
              <AccordionTrigger className="text-sm font-semibold text-[#0A1E3F] hover:no-underline py-5" data-testid="faq-trigger-3">
                Can corporate professionals also book?
              </AccordionTrigger>
              <AccordionContent className="text-xs text-gray-600 leading-relaxed pb-5">
                Yes! We provide executive-level coaching and roadmap recalibration for corporate professionals seeking faster growth, leadership communication, and strategic job switching.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="faq-4" className="bg-white rounded-2xl border border-gray-200/50 overflow-hidden px-6">
              <AccordionTrigger className="text-sm font-semibold text-[#0A1E3F] hover:no-underline py-5" data-testid="faq-trigger-4">
                Will comprehensive interview guidance be provided?
              </AccordionTrigger>
              <AccordionContent className="text-xs text-gray-600 leading-relaxed pb-5">
                Yes, absolutely. We cover HR behavioral questions, mock interviews, confidence building, presentation grooming, and exhaustive resume analytical feedback.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="faq-5" className="bg-white rounded-2xl border border-gray-200/50 overflow-hidden px-6">
              <AccordionTrigger className="text-sm font-semibold text-[#0A1E3F] hover:no-underline py-5" data-testid="faq-trigger-5">
                Is the career counselling personalized?
              </AccordionTrigger>
              <AccordionContent className="text-xs text-gray-600 leading-relaxed pb-5">
                Entirely. Every single advice, roadmap, and milestone plan is personalized and constructed strictly based on your individual skills, profile, and ultimate career goals.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Sticky/Floating WhatsApp Button */}
      <button 
        onClick={() => handleWhatsAppClick("whatsapp-floating", "Hello Jijivisha Consulting, I'd like to book a career guidance session.")}
        className="fixed bottom-6 right-6 z-50 bg-[#25D366] text-white p-4 rounded-full shadow-2xl hover:scale-110 active:scale-95 transition-all flex items-center justify-center duration-300 group"
        title="Chat on WhatsApp"
        data-testid="floating-whatsapp-button"
      >
        <MessageSquare size={24} className="group-hover:rotate-12 transition-transform" />
        <span className="max-w-0 overflow-hidden group-hover:max-w-xs transition-all duration-300 ease-out text-[11px] font-bold uppercase tracking-wider pl-0 group-hover:pl-2 whitespace-nowrap">
          WhatsApp Us
        </span>
      </button>

      {/* Footer */}
      <footer className="bg-[#0A1E3F] text-gray-400 py-12 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-4 gap-8">
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Jijivisha Logo" className="h-10 w-10 object-contain rounded-lg bg-white/10 p-1" />
              <span className="font-display font-bold text-lg text-white tracking-tight">Jijivisha Consulting</span>
            </div>
            <p className="text-xs leading-relaxed font-light text-gray-400">
              Transforming Careers with 28+ Years of Expertise. Leading mentorship, corporate legal and HR training solutions.
            </p>
          </div>

          <div>
            <h4 className="font-semibold text-xs text-white uppercase tracking-wider mb-4">Quick Links</h4>
            <ul className="space-y-2 text-xs">
              <li><a href="#home" className="hover:text-white transition-colors">Home</a></li>
              <li><a href="#about" className="hover:text-white transition-colors">About Anand</a></li>
              <li><a href="#services" className="hover:text-white transition-colors">Services Offered</a></li>
              <li><a href="#testimonials" className="hover:text-white transition-colors">Testimonials</a></li>
              <li><a href="#contact" className="hover:text-white transition-colors">Book Consultation</a></li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-xs text-white uppercase tracking-wider mb-4">Core Numbers</h4>
            <ul className="space-y-2 text-xs">
              <li>Phone: 7295047308</li>
              <li>Booking WhatsApp: 9534263712</li>
              <li>Email: jijivishaconsulting@gmail.com</li>
            </ul>
          </div>

          <div className="space-y-4">
            <h4 className="font-semibold text-xs text-white uppercase tracking-wider">Admin portal</h4>
            <a 
              href="/admin" 
              className="inline-flex items-center gap-2 text-xs text-[#D4AF37] hover:underline"
              data-testid="nav-admin-login-link"
            >
              Secure Staff Login
            </a>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 mt-12 pt-6 border-t border-white/5 flex flex-col sm:flex-row justify-between items-center gap-4 text-xs">
          <p>© 2026 Jijivisha Consulting. All Rights Reserved.</p>
          <div className="flex gap-4">
            <span className="text-gray-500 hover:text-gray-300 cursor-pointer">Privacy Policy</span>
            <span className="text-gray-500 hover:text-gray-300 cursor-pointer">Terms of Service</span>
          </div>
        </div>
      </footer>

    </div>
  );
}
